<template>
  <li :class="itemClasses" @click="handleToggleOption" @keydown="handleKeyEvent">
    <BravadoSelectDropdownImage
      v-if="option.selectDropdownImage"
      :image="option.selectDropdownImage"
      class="bravado-select-dropdown-item__image"
    />
    <div :class="textClasses">
      <BravadoText variant="body">
        {{ option.selectDropdownTitle }}
      </BravadoText>
      <BravadoText variant="caption2" class="bravado-select-dropdown-item__helper-text">
        {{ option.selectDropdownHelperText }}
      </BravadoText>
    </div>
    <CheckIcon v-if="isSelected" :class="checkClasses" />
  </li>
</template>

<script lang="ts" setup>
import { computed } from '@nuxtjs/composition-api';
import type { BravadoSelectDropdownOption } from './types';
import BravadoSelectDropdownImage from './BravadoSelectDropdownImage.vue';
import CheckIcon from '@/assets/svg/check.vue.svg';

type BravadoSelectDropdownItemProps = {
  option: BravadoSelectDropdownOption;
  isSelected?: boolean;
  removable?: boolean;
};
type BravadoSelectDropdownItemEmits = {
  (event: 'toggle-option', payload: MouseEvent): void;
  (event: 'keydown', payload: KeyboardEvent): void;
};

const props = withDefaults(defineProps<BravadoSelectDropdownItemProps>(), {
  isSelected: false,
  removable: true,
});
const emit = defineEmits<BravadoSelectDropdownItemEmits>();

function handleToggleOption(event: MouseEvent) {
  emit('toggle-option', event);
}

function handleKeyEvent(event: KeyboardEvent) {
  emit('keydown', event);
}

const itemClasses = computed(() => ({
  'bravado-select-dropdown-item': true,
  'bravado-select-dropdown-item--with-helper-text': !!props.option.selectDropdownHelperText,
  'bravado-select-dropdown-item--not-removable': !props.removable,
}));
const textClasses = computed(() => ({
  'bravado-select-dropdown-item__text': true,
  'bravado-select-dropdown-item__text--not-removable': !props.removable,
}));
const checkClasses = computed(() => ({
  'bravado-select-dropdown-item__check': true,
  'bravado-select-dropdown-item__check--not-removable': !props.removable,
}));
</script>

<style scoped lang="scss">
.bravado-select-dropdown-item {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  color: var(--theme-color-text);
  background: var(--theme-color-card);

  &--with-helper-text {
    padding: 5px 16px;
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 3px 1px var(--blue-60);
  }

  &:focus,
  &:hover {
    background: var(--theme-color-over-card);
  }

  &:hover {
    cursor: pointer;
  }

  &--not-removable {
    &:focus,
    &:hover {
      cursor: not-allowed;
      background: var(--theme-color-card);
    }
  }

  &__image {
    margin-right: 8px;
  }

  &__text {
    display: flex;
    flex-direction: column;

    &--not-removable {
      opacity: 0.5;
    }
  }

  &__helper-text {
    color: var(--gray-50);
  }

  &__check {
    color: var(--theme-color-text);
    opacity: 0.5;
    margin-left: auto;

    &--not-removable {
      opacity: 0.2;
    }
  }
}
</style>
